<template>
<div style="width: 100%">
	<v-subheader>{{$t('workers.title')}}</v-subheader>
	<v-tooltip top slot="activator" v-if="!permission">
		<v-btn
			:loading="isLoadingCreateWorker"
			slot="activator"
			@click="addWorker"
			color="indigo lighten-3"
			dark
			fab
		>
			<v-icon>add</v-icon>
		</v-btn>
		<span>{{$t('workers.add_worker')}}</span>
	</v-tooltip>
	<v-card style="width: 100%">
		<v-card-title>
			<v-layout row wrap>
				<v-flex md8 sm12>
					<workers-filter
						:agency="selectedAgency"
						:employer="selectedEmployer"
						:jobDateMonth="jobDateMonth"
						:jobDateWeek="jobDateWeek"
						@changeSelectedAgency="changeSelectedAgency"
						@changeSelectedEmployer="changeSelectedEmployer"
						@changeJobDateMonth="changeJobDateMonth"
						@changeJobDateWeek="changeJobDateWeek"
					/>
				</v-flex>
				<v-flex md4 sm12>
					<v-text-field
						v-model="search"
						append-icon="search"
						:label="$t('workers.search')"
						single-line
						hide-details
					/>
				</v-flex>
				<v-flex xs12>
					<div class="checkAll"
						v-if="!hasRole('canPrepay')"
					>
						<v-checkbox class="checkboxRole"
							:disabled="$store.state.workers.selectedWorkerIds.length == 0"
							v-model="showOnlySelected"
							label="Show only selected"
						/>
						<v-btn
						:disabled="$store.state.workers.selectedWorkerIds.length == 0"
						@click="clearSelected()"
						>
							Clear selected
						</v-btn>
						<v-btn
							slot="activator"
							color="primary"
							dark
							class="export-button"
							v-if="!mobile"
							@click="showExportMenu = true"
						>
							<v-icon>cloud_download</v-icon> &nbsp;
							{{ $t('workers.filters.exportButton') }}
						</v-btn>
					</div>
				</v-flex>
			</v-layout>
		</v-card-title>
		<v-data-table
			:headers="$vuetify.breakpoint.xs ? headersSmall : headers"
			:items="workers"
			:pagination.sync="pagination"
			:loading="loading"
			:total-items="totalItems"
			:rows-per-page-items="rowsPerPageItems"
		>
			<template slot="headers" slot-scope="props">
				<tr>
					<th
						v-for="header in props.headers"
						:key="header.text"
						:class="[
							'table-head-column column sortable text-xs-' +
							(header.align ? header.align : 'left'),
							pagination.descending ? 'desc' : 'asc',
							header.value === pagination.sortBy ? 'active' : ''
						]"
						@click="changeSort(header.value, header.sortable)"
					>
						<v-icon small v-if="header.sortable">arrow_upward</v-icon>
						{{ header.text }}
					</th>
				</tr>
			</template>
			<template slot="items" slot-scope="props">
				<tr @dblclick="editWorker(props.item.id)">
					<td>{{ props.item.id }}</td>
					<td>{{ props.item.firstname }}</td>
					<td>{{ props.item.lastname }}</td>
					<td>{{ props.item.jobs.length > 0 ? props.item.jobs.slice(-1)[0].job_start : '-' }}</td>
					<td>{{ props.item.jobs.length > 0 ? props.item.jobs.slice(-1)[0].job_end : '-' }}</td>
					<td>{{ props.item.agency ? props.item.agency.name : '' }}</td>
					<td v-if="!$vuetify.breakpoint.xs">
						{{ props.item.gender ? $t('common.gender_' + props.item.gender) : '' }}
					</td>
					<td v-if="!hasRole('canPrepay')">
						<exportable-checkbox
							:workerId="props.item.id"
						/>
					</td>
					<td class="text-xs-right">
						<v-btn fab dark small
							color="indigo lighten-2"
							:to="'/workers/' + props.item.id"
							class="action-button"
						>
							<v-icon dark>edit</v-icon>
						</v-btn>
						<v-btn fab dark small
							v-if="!mobile"
							color="indigo"
							:to="'/workers/' + props.item.id + '#finance'"
							class="action-button"
						>
							<v-icon dark>attach_money</v-icon>
						</v-btn>
						<ConfirmDialog class="delete-button"
							v-if="!mobile && !hasRole('canPrepay')"
							v-bind="propConfirmDialog"
							@accept="onDeleteWorker(props.item.id)"
							:tooltip="$t('common.delete')"
						>
							<v-icon dark>delete</v-icon>
						</ConfirmDialog>
					</td>
				</tr>
			</template>
			<div slot="no-results" :value="true" color="error" icon="warning">
				{{$t('common.no_result', {search})}}
			</div>
		</v-data-table>
	</v-card>
	<export-menu
		v-model="showExportMenu"
		@export="exportFilterWorkers"
	/>
</div>
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex'
import { localeHeadersWorkers } from '../utils/helpers'
import { TEMPLATE_WORKER, HEADERS_TABLE_WORKERS } from '../constants'
import ConfirmDialog from '../components/ConfirmDialog'
import { notify } from '../utils/notifications'
import WorkersFilter from './workers/WorkersFilter'
import ExportableCheckbox from './workers/ExportableCheckbox'
import ExportMenu from './workers/ExportMenu'

export default {
	components: {
		ConfirmDialog,
		WorkersFilter,
		ExportableCheckbox,
		ExportMenu
	},
	data: vm => ({
		showOnlySelected: false,
		propConfirmDialog: {
			message: vm.$t('workers.confirm_message_worker'),
			title: vm.$t('common.confirm'),
			btnProps: {
				fab: true,
				dark: true,
				small: true,
				color: 'red lighten-2',
			}
		},
		search: '',
		pagination: {
			page: 1,
			rowsPerPage: 50,
			sortBy: 'id',
			descending: false
		},
		selectedAgency: null,
		selectedEmployer: null,
		jobDateMonth: null,
		jobDateWeek: null,
		totalItems: 0,
		rowsPerPageItems: [50, 100, 200, 500],
		mobile: false,
		showExportMenu: false
	}),
	computed: {
		...mapGetters({ hasRole: 'auth/hasRole' }),
		...mapState({
			workers: ({ workers }) => workers.workers,
			meta: ({ workers }) => workers.meta,
			loading: ({ workers }) => workers.loading,
			isLoadingCreateWorker: ({ workers }) => workers.isLoadingCreateWorker
		}),
		headers() {
			let headerList = localeHeadersWorkers(HEADERS_TABLE_WORKERS, this.$t)
			if(this.hasRole('canPrepay')) {
				headerList = headerList.filter(item => item.value !== 'mark')
			}
			return headerList
		},
		headersSmall() {
			return this.headers.filter(({ value }) => value !== 'gender')
		},
		permission() {
			return this.hasRole('canPrepay')
		}
	},
	created() {
		let query = this.$route.query
		this.search = query.search
		if(query.agency_id) {
			this.selectedAgency.id = parseInt(query.agency_id)
		}
		if(query.employer_id) {
			this.selectedEmployer.id = parseInt(query.employer_id)
		}
		if(query.jobDateMonth) {
			this.jobDateMonth = query.jobDateMonth
		}
		if(query.jobDateWeek) {
			this.jobDateWeek = query.jobDateWeek
		}
		this.pushQuery(query)
		if(screen.width < 640) {
			this.mobile = true
		}
	},
	destroyed() {
		this.setList({
			data: []
		})
	},
	methods: {
		...mapActions({
			fetch: 'workers/fetch',
			createWorker: 'workers/createWorker',
			deleteWorker: 'workers/deleteWorker',
			exportWorkers: 'workers/exportWorkers'
		}),
		...mapMutations({
			setList: 'workers/setList'
		}),
		addWorker() {
			this.createWorker({ ...TEMPLATE_WORKER })
		},
		changeSort (column, sortable) {
			if(!sortable) {
				return
			}
			if (this.pagination.sortBy === column) {
				this.pagination.descending = !this.pagination.descending
			} else {
				this.pagination.sortBy = column
				this.pagination.descending = false
			}
		},
		clearSelected() {
			this.$store.dispatch('workers/clearSelected')
			this.showOnlySelected = false
		},
 		updatePagination() {
			let query = {...this.$route.query }
			if (this.showOnlySelected) {
				query['selected_worker_ids'] = this.$store.state.workers.selectedWorkerIds
			}
			
			this.fetch(query)
				.then(() => {
					this.totalItems = this.meta ? this.meta.total : null
				})
		},
		pushQuery (old_query = null) {
			let query = {
				count: this.pagination.rowsPerPage.toString(),
				offset: (this.pagination.rowsPerPage * (this.pagination.page - 1)).toString(),
				sortBy: this.pagination.sortBy,
				descending: this.pagination.descending ? 'asc' : 'desc'
			}
			this.selectedAgency ? query.agency_id = this.selectedAgency.id : '',
			this.selectedEmployer ? query.employer_id = this.selectedEmployer.id : '',
			this.jobDateMonth ? query.jobDateMonth = this.jobDateMonth: ''
			this.jobDateWeek ? query.jobDateWeek = this.jobDateWeek: ''
			this.search ? query.search = this.search.toLowerCase() : '',
			this.$router.push({ query: query })
			if(JSON.stringify(old_query) == JSON.stringify(query)) {
				this.updatePagination()
			}
		},
		onDeleteWorker(id) {
			this.deleteWorker(id).then(() => {
				this.$notify({
				...notify,
				text: this.$t('notifications.deleted', { type: this.$t('workers.worker') }),
				})
			})
		},
		editWorker(id) {
			this.$router.push({ name: "workerDetailDetail", params: { workerId: id } })
		},
		changeSelectedAgency(value) {
			this.selectedAgency = value
		},
		changeSelectedEmployer(value) {
			this.selectedEmployer = value
		},
		changeJobDateMonth(value) {
			this.jobDateMonth = value
		},
		changeJobDateWeek(value) {
			this.jobDateWeek = value
		},
		exportFilterWorkers (params) {
			let query = {
				sortBy: this.$route.query.sortBy,
				descending: this.$route.query.descending,
				selected_worker_ids: this.$store.state.workers.selectedWorkerIds,
				...params
			}

			this.jobDateMonth ? query.jobDateMonth = this.jobDateMonth : null
			this.jobDateWeek ? query.jobDateWeek = this.jobDateWeek : null
			this.selectedAgency ? query.agency_id = this.selectedAgency.id : ''
			this.selectedEmployer ? query.employer_id = this.selectedEmployer.id : null,
			this.$route.query.search ? query.search = this.$route.query.search : null,

			this.exportWorkers(query)
		},
	},
	watch: {
		showOnlySelected () {
			this.updatePagination()
		},
		'pagination.page' () {
			this.pushQuery()
		},
		'pagination.rowsPerPage' () {
			this.pushQuery()
		},
		'pagination.descending' () {
			this.pushQuery()
		},
		'$route.query'() {
			this.updatePagination()
		},
		selectedAgency () {
			this.pushQuery()
		},
		selectedEmployer () {
			this.pushQuery()
		},
		jobDateMonth () {
			this.pushQuery()
		},
		jobDateWeek () {
			this.pushQuery()
		},
		search () {
			this.pushQuery()
		}
	}
}
</script>

<style>
	.delete-button {
		float: left;
	}
	.action-button,
	.delete-button button {
		margin: 3px !important;
		width: 35px !important;
		height: 35px !important;
		float: left;
	}
	.checkAll {
		display: block;
		width: 35%;
		float: right;
		text-align: center;
	}
	.checkAll .checkboxRole {
		margin: 6px 0 0 0;
		padding: 0;
		float: right;
	}
	.checkAll .checkboxRole .v-input__slot {
		margin: 0;
	}
	.checkAll .checkboxRole .v-messages {
		display: none;
	}
	.checkAll .v-progress-circular {
		margin-left: 40px;
	}
	.export-button {
		float: right;
		margin: 0px 8px !important;
	}
</style>
